// src/features/tools/ToolsList.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Tab, Tabs } from 'react-bootstrap';
import { FaSave, FaPlus, FaCubes, FaTags, FaUser, FaBriefcase } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import TableWrapper from '../../components/TableWrapper';
import FilterSelect from '../../components/FilterSelect';
import ToolsService from './ToolsService';
import CustomSnackbar from '../../components/CustomSnackbar';
import ExportCsvButton from '../../components/ExportCsvButton';
import { TIPOS_EQUIPOS, getTipoDescripcion } from '../../utils/constants';
import { useColumnFilter } from '../../hooks/useColumnFilter';
import { useNavigationHistory } from '../../hooks/useNavigationHistory';

function ToolsList() {
  const [tools, setTools] = useState([]);
  const [assignedTools, setAssignedTools] = useState([]);
  const [filteredTools, setFilteredTools] = useState([]);
  const [filteredAssignedTools, setFilteredAssignedTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [activeTab, setActiveTab] = useState('stock');
  const [editMode, setEditMode] = useState(false);
  const [tool, setTool] = useState({
    id: '',
    name: '',
    type: '',
    quantity: ''
  });
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, type: '', message: '' });
  const [stockGridApi, setStockGridApi] = useState(null);
  const [assignedGridApi, setAssignedGridApi] = useState(null);
  const inputRef = useRef(null);
  const stockGridRef = useRef(null);
  const assignedGridRef = useRef(null);
  const navigate = useNavigate();
  const { saveCurrentRoute } = useNavigationHistory();

  // Value getter para el tipo de Herramienta - mapea códigos a descripciones
  const typeValueGetter = useCallback((item) => {
    const tipo = TIPOS_EQUIPOS.find(t => t.id === item.type);
    return tipo ? tipo.descripcion : item.type;
  }, []);

  // Value getter para herramientas asignadas - tipo de herramienta
  const assignedTypeValueGetter = useCallback((item) => {
    const tipo = TIPOS_EQUIPOS.find(t => t.id === item.tool?.type);
    return tipo ? tipo.descripcion : item.tool?.type || '';
  }, []);

  // Value getter para herramientas asignadas - nombre de herramienta
  const assignedNameValueGetter = useCallback((item) => {
    return item.tool?.name || '';
  }, []);

  // Value getters únicos y consolidados
  const toolTypeValueGetter = useCallback((item) => {
    // Funciona tanto para herramientas de stock como asignadas
    const typeId = item.type || item.tool?.type;
    const tipo = TIPOS_EQUIPOS.find(t => t.id === typeId);
    return tipo ? tipo.descripcion : typeId;
  }, []);

  const assignedToolNameValueGetter = useCallback((item) => {
    return item.tool?.name || '';
  }, []);

  const assignedToTypeValueGetter = useCallback((item) => {
    if (item.person) return 'Persona';
    if (item.work) return 'Obra';
    return 'Sin asignar';
  }, []);

  // Configuración de filtros persistentes para STOCK
  const { 
    filterOptions: nameOptions, 
    activeFilters: activeNames,
    handleOptionsUpdate: handleNameOptionsUpdate,
    handleFiltersChange: handleNameChange,
    filterData: filterByName 
  } = useColumnFilter(tools, 'name', (item) => item.name, 'tools-stock');

  const { 
    filterOptions: typeOptions, 
    activeFilters: activeTypes,
    handleOptionsUpdate: handleTypeOptionsUpdate,
    handleFiltersChange: handleTypeChange,
    filterData: filterByType 
  } = useColumnFilter(tools, 'type', toolTypeValueGetter, 'tools-stock');

  // Configuración de filtros persistentes para ASIGNADAS
  const { 
    filterOptions: assignedNameOptions, 
    activeFilters: activeAssignedNames,
    handleOptionsUpdate: handleAssignedNameOptionsUpdate,
    handleFiltersChange: handleAssignedNameChange,
    filterData: filterAssignedByName 
  } = useColumnFilter(assignedTools, 'tool.name', assignedToolNameValueGetter, 'tools-assigned');

  const { 
    filterOptions: assignedTypeOptions, 
    activeFilters: activeAssignedTypes,
    handleOptionsUpdate: handleAssignedTypeOptionsUpdate,
    handleFiltersChange: handleAssignedTypeChange,
    filterData: filterAssignedByType 
  } = useColumnFilter(assignedTools, 'tool.type', toolTypeValueGetter, 'tools-assigned');

  const { 
    filterOptions: assignedToTypeOptions, 
    activeFilters: activeAssignedToTypes,
    handleOptionsUpdate: handleAssignedToTypeOptionsUpdate,
    handleFiltersChange: handleAssignedToTypeChange,
    filterData: filterAssignedByToType 
  } = useColumnFilter(assignedTools, 'assignedToType', assignedToTypeValueGetter, 'tools-assigned');

  // Cargar datos al montar el componente
  useEffect(() => {
    loadTools();
  }, []);

  // Cargar herramientas asignadas cuando se selecciona la pestaña
  useEffect(() => {
    if (activeTab === 'assigned' && assignedTools.length === 0) {
      loadAssignedTools();
    }
  }, [activeTab, assignedTools.length]);

  // Aplicar filtros de STOCK cuando cambian los datos o los filtros activos
  useEffect(() => {
    if (!tools || tools.length === 0) return;
    
    // Comenzar con todos los datos originales
    let result = [...tools];
    
    // Aplicar filtros en cadena solo si hay filtros activos
    if (activeNames && activeNames.length > 0) {
      result = filterByName(result);
    }
    
    if (activeTypes && activeTypes.length > 0) {
      result = filterByType(result);
    }
    
    // Actualizar el estado solo si ha cambiado el resultado
    const resultIds = result.map(item => item.id).sort().join(',');
    const currentIds = filteredTools.map(item => item.id).sort().join(',');
    
    if (resultIds !== currentIds) {
      setFilteredTools(result);
    }
  }, [
    tools, 
    activeNames, 
    activeTypes,
    filterByName,
    filterByType,
    filteredTools
  ]);

  // Aplicar filtros de ASIGNADAS cuando cambian los datos o los filtros activos
  useEffect(() => {
    if (!assignedTools || assignedTools.length === 0) return;
    
    // Comenzar con todos los datos originales
    let result = [...assignedTools];
    
    // Aplicar filtros en cadena solo si hay filtros activos
    if (activeAssignedNames && activeAssignedNames.length > 0) {
      result = filterAssignedByName(result);
    }
    
    if (activeAssignedTypes && activeAssignedTypes.length > 0) {
      result = filterAssignedByType(result);
    }

    if (activeAssignedToTypes && activeAssignedToTypes.length > 0) {
      result = filterAssignedByToType(result);
    }
    
    // Actualizar el estado solo si ha cambiado el resultado
    const resultIds = result.map(item => item.id).sort().join(',');
    const currentIds = filteredAssignedTools.map(item => item.id).sort().join(',');
    
    if (resultIds !== currentIds) {
      setFilteredAssignedTools(result);
    }
  }, [
    assignedTools, 
    activeAssignedNames, 
    activeAssignedTypes,
    activeAssignedToTypes,
    filterAssignedByName,
    filterAssignedByType,
    filterAssignedByToType,
    filteredAssignedTools
  ]);

  // Efecto para enfocar el campo de cantidad en modo edición
  useEffect(() => {
    if (editMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editMode]);

  // Función para guardar el estado de las grillas
  const saveGridStates = useCallback(() => {
    try {
      if (stockGridRef.current?.api) {
        const stockState = stockGridRef.current.api.getColumnState();
        const stockFilterModel = stockGridRef.current.api.getFilterModel();
        sessionStorage.setItem('tools-stock-grid-state', JSON.stringify({
          columnState: stockState,
          filterModel: stockFilterModel
        }));
      }

      if (assignedGridRef.current?.api) {
        const assignedState = assignedGridRef.current.api.getColumnState();
        const assignedFilterModel = assignedGridRef.current.api.getFilterModel();
        sessionStorage.setItem('tools-assigned-grid-state', JSON.stringify({
          columnState: assignedState,
          filterModel: assignedFilterModel
        }));
      }

      // También guardar la pestaña activa
      sessionStorage.setItem('tools-active-tab', activeTab);
    } catch (error) {
      console.error('Error al guardar estado de grillas:', error);
    }
  }, [activeTab]);

  // Restaurar pestaña activa solo una vez al montar el componente
  useEffect(() => {
    const savedTab = sessionStorage.getItem('tools-active-tab');
    if (savedTab && (savedTab === 'stock' || savedTab === 'assigned')) {
      setActiveTab(savedTab);
    }
  }, []); // Solo se ejecuta una vez al montar

  // Restaurar estado de grilla de stock cuando se cargan los datos
  useEffect(() => {
    if (tools.length > 0 && stockGridRef.current?.api && activeTab === 'stock') {
      // Pequeño delay para asegurar que la grilla esté completamente inicializada
      const timeoutId = setTimeout(() => {
        try {
          const savedStockState = sessionStorage.getItem('tools-stock-grid-state');
          if (savedStockState && stockGridRef.current?.api) {
            const { columnState, filterModel } = JSON.parse(savedStockState);
            if (columnState) {
              stockGridRef.current.api.applyColumnState({ state: columnState });
            }
            if (filterModel) {
              stockGridRef.current.api.setFilterModel(filterModel);
            }
          }
        } catch (error) {
          console.error('Error al restaurar estado de grilla stock:', error);
        }
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [tools.length, activeTab]); // Cambiar tools por tools.length para evitar renders innecesarios

  // Restaurar estado de grilla asignadas cuando se cargan los datos
  useEffect(() => {
    if (assignedTools.length > 0 && assignedGridRef.current?.api && activeTab === 'assigned') {
      // Pequeño delay para asegurar que la grilla esté completamente inicializada
      const timeoutId = setTimeout(() => {
        try {
          const savedAssignedState = sessionStorage.getItem('tools-assigned-grid-state');
          if (savedAssignedState && assignedGridRef.current?.api) {
            const { columnState, filterModel } = JSON.parse(savedAssignedState);
            if (columnState) {
              assignedGridRef.current.api.applyColumnState({ state: columnState });
            }
            if (filterModel) {
              assignedGridRef.current.api.setFilterModel(filterModel);
            }
          }
        } catch (error) {
          console.error('Error al restaurar estado de grilla asignadas:', error);
        }
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [assignedTools.length, activeTab]); // Cambiar assignedTools por assignedTools.length

  // Limpiar estados guardados al desmontar el componente
  useEffect(() => {
    return () => {
      // Guardar estado final antes de desmontar
      try {
        if (stockGridRef.current?.api) {
          const stockState = stockGridRef.current.api.getColumnState();
          const stockFilterModel = stockGridRef.current.api.getFilterModel();
          sessionStorage.setItem('tools-stock-grid-state', JSON.stringify({
            columnState: stockState,
            filterModel: stockFilterModel
          }));
        }

        if (assignedGridRef.current?.api) {
          const assignedState = assignedGridRef.current.api.getColumnState();
          const assignedFilterModel = assignedGridRef.current.api.getFilterModel();
          sessionStorage.setItem('tools-assigned-grid-state', JSON.stringify({
            columnState: assignedState,
            filterModel: assignedFilterModel
          }));
        }

        // Guardar pestaña activa
        sessionStorage.setItem('tools-active-tab', activeTab);
      } catch (error) {
        console.error('Error al guardar estado final:', error);
      }
    };
  }, [activeTab]);

  // Función para cargar la lista de Herramientas
  const loadTools = async () => {
    try {
      setLoading(true);
      const data = await ToolsService.getAllTools();
      setTools(data);
      setFilteredTools(data);
    } catch (error) {
      console.error('Error al cargar Herramientas:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar la lista de Herramientas'
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para cargar herramientas asignadas
  const loadAssignedTools = async () => {
    try {
      setLoadingAssigned(true);
      const data = await ToolsService.getAllAssignedTools();
      setAssignedTools(data);
      setFilteredAssignedTools(data);
    } catch (error) {
      console.error('Error al cargar herramientas asignadas:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: 'Error al cargar las herramientas asignadas'
      });
    } finally {
      setLoadingAssigned(false);
    }
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "quantity") {
      setTool({ ...tool, [name]: parseInt(value) || '' });
    } else {
      setTool({ ...tool, [name]: value });
    }
  };

  // Validar el formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!tool.name.trim()) newErrors.name = true;
    if (!tool.type) newErrors.type = true;
    if (!tool.quantity || tool.quantity <= 0) newErrors.quantity = true;
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Guardar los cambios (crear o actualizar)
  const handleSaveChanges = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      if (editMode) {
        // Actualizar Herramientas existente
        await ToolsService.updateTool(tool.id, tool);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Herramienta actualizada con éxito'
        });
      } else {
        // Crear nuevo Herramientas
        await ToolsService.createTool(tool);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Herramienta agregada con éxito'
        });
      }
      
      // Resetear formulario y recargar datos
      setTool({
        id: '',
        name: '',
        type: '',
        quantity: ''
      });
      setEditMode(false);
      loadTools();
    } catch (error) {
      console.error('Error al guardar Herramienta:', error);
      setSnackbar({
        open: true,
        type: 'error',
        message: `Error al ${editMode ? 'actualizar' : 'agregar'} Herramienta`
      });
    } finally {
      setLoading(false);
    }
  };

  // Seleccionar Herramienta para edición
  const handleEditTool = (selectedTool) => {
    setTool({
      id: selectedTool.id,
      name: selectedTool.name,
      type: selectedTool.type,
      quantity: selectedTool.quantity
    });
    setEditMode(true);
  };

  // Eliminar Herramienta
  const handleDeleteTool = async (selectedTool) => {
    if (window.confirm(`¿Está seguro que desea eliminar la Herramienta: ${selectedTool.name}?`)) {
      try {
        await ToolsService.deleteTool(selectedTool.id);
        setSnackbar({
          open: true,
          type: 'success',
          message: 'Herramienta eliminada con éxito'
        });
        loadTools();
      } catch (error) {
        console.error('Error al eliminar Herramienta:', error);
        setSnackbar({
          open: true,
          type: 'error',
          message: 'Error al eliminar el Herramienta'
        });
      }
    }
  };

  // Mostrar detalles de Herramienta retirados
  const handleShowRetiredTools = (tool) => {
    if (tool.retired > 0) {
      // Aquí iría la lógica para mostrar las Herramientas retirados
      //console.log('Mostrar Herramientas retiradas para:', tool.id);
    }
  };

  // Navegar al perfil de persona
  const handleViewPerson = (assignedTool) => {
    if (assignedTool.person?.id) {
      // Guardar el estado de las grillas antes de navegar
      saveGridStates();
      saveCurrentRoute(`/people/${assignedTool.person.id}`);
    }
  };

  // Navegar al perfil de obra
  const handleViewWork = (assignedTool) => {
    if (assignedTool.work?.id) {
      // Guardar el estado de las grillas antes de navegar
      saveGridStates();
      saveCurrentRoute(`/works/${assignedTool.work.id}`);
    }
  };

  // Manejar cambio de pestaña
  const handleTabChange = (tab) => {
    // Solo cambiar si es diferente a la pestaña actual
    if (tab !== activeTab) {
      // Guardar estado actual antes de cambiar
      saveGridStates();
      setActiveTab(tab);
    }
  };

  // Cerrar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Configuración de columnas para la tabla de STOCK
  const stockColumnDefs = useMemo(() => [
    { 
      headerName: 'Nombre', 
      field: 'name', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        debounceMs: 0,
        buttons: []
      }
    },
    { 
      headerName: 'Tipo de Herramienta', 
      field: 'type', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        debounceMs: 0,
        buttons: []
      },
      cellRenderer: (params) => {
        const tipo = TIPOS_EQUIPOS.find(t => t.id === params.value);
        return tipo ? tipo.descripcion : params.value;
      }
    },
    { 
      headerName: 'Stock', 
      field: 'quantity', 
      width: 100,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        debounceMs: 0,
        buttons: []
      }
    },
    { 
      headerName: 'Asignadas', 
      field: 'assigned', 
      width: 120,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        debounceMs: 0,
        buttons: []
      }
    },
    { 
      headerName: 'Retiradas', 
      field: 'retired', 
      width: 120,
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        debounceMs: 0,
        buttons: []
      },
      cellRenderer: (params) => {
        if (params.value > 0) {
          return (
            <div className="d-flex align-items-center">
              <span className="me-2">{params.value}</span>
              <Button 
                variant="outline-primary" 
                size="sm" 
                onClick={() => handleShowRetiredTools(params.data)}
                style={{ padding: '0.1rem 0.4rem', fontSize: '0.7rem' }}
              >
                Ver
              </Button>
            </div>
          );
        }
        return params.value;
      }
    }
  ], []);

  // Configuración de columnas para la tabla de ASIGNADAS
  const assignedColumnDefs = useMemo(() => [
    { 
      headerName: 'Herramienta', 
      field: 'tool.name', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        debounceMs: 0,
        buttons: []
      },
      valueGetter: (params) => params.data.tool?.name || '',
    },
    { 
      headerName: 'Tipo', 
      field: 'tool.type', 
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        debounceMs: 0,
        buttons: []
      },
      cellRenderer: (params) => {
        const tipo = TIPOS_EQUIPOS.find(t => t.id === params.data.tool?.type);
        return tipo ? tipo.descripcion : params.data.tool?.type || '';
      },
      valueGetter: (params) => {
        const tipo = TIPOS_EQUIPOS.find(t => t.id === params.data.tool?.type);
        return tipo ? tipo.descripcion : params.data.tool?.type || '';
      }
    },
    { 
      headerName: 'Serial', 
      field: 'serial', 
      width: 120,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        debounceMs: 0,
        buttons: []
      },
      cellRenderer: (params) => params.value || 'N/A'
    },
    { 
      headerName: 'Asignado a', 
      field: 'assignedTo', 
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        debounceMs: 0,
        buttons: []
      },
      cellRenderer: (params) => {
        const data = params.data;
        
        if (data.person) {
          const { firstName, surname, secondName, secondSurname } = data.person;
          const fullName = `${firstName} ${surname} ${secondName || ''} ${secondSurname || ''}`.trim();
          
          return (
            <div className="d-flex align-items-center">
              <FaUser 
                className="me-2 text-primary"
                style={{ cursor: 'pointer' }}
                onClick={() => handleViewPerson(data)}
                title="Ver perfil de persona"
              />
              <Button 
                variant="link" 
                className="p-0 text-start text-dark"
                style={{ 
                  textDecoration: 'none',
                  color: '#000 !important',
                  fontWeight: 'normal'
                }}
                onClick={() => handleViewPerson(data)}
                title="Ver perfil de persona"
              >
                {fullName}
              </Button>
            </div>
          );
        }
        
        if (data.work) {
          return (
            <div className="d-flex align-items-center">
              <FaBriefcase 
                className="me-2 text-success"
                style={{ cursor: 'pointer' }}
                onClick={() => handleViewWork(data)}
                title="Ver perfil de obra"
              />
              <Button 
                variant="link" 
                className="p-0 text-start text-dark"
                style={{ 
                  textDecoration: 'none',
                  color: '#000 !important',
                  fontWeight: 'normal'
                }}
                onClick={() => handleViewWork(data)}
                title="Ver perfil de obra"
              >
                {data.work.name}
              </Button>
            </div>
          );
        }
        
        return 'Sin asignar';
      },
      valueGetter: (params) => {
        const data = params.data;
        if (data.person) {
          const { firstName, surname, secondName, secondSurname } = data.person;
          return `${firstName} ${surname} ${secondName || ''} ${secondSurname || ''}`.trim();
        }
        if (data.work) {
          return data.work.name;
        }
        return 'Sin asignar';
      }
    }
  ], []);

  // Contenido memoizado para la tabla de stock
  const renderStockTableContent = useMemo(() => {
    if (loading && filteredTools.length === 0) {
      return (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando herramientas...</p>
        </div>
      );
    }

    return (
      <TableWrapper
        ref={stockGridRef}
        columnDefs={stockColumnDefs}
        rowData={filteredTools}
        tableType="equipos"
        onGridReady={(e) => setStockGridApi(e.api)}
        onEdit={handleEditTool}
        onDelete={handleDeleteTool}
        customGridOptions={{
          animateRows: true,
          pagination: true,
          paginationPageSize: 25,
          domLayout: 'autoHeight',
          defaultColDef: {
            resizable: true,
            sortable: true,
            filter: true,
            floatingFilter: true
          }
        }}
        style={{ 
          marginBottom: '20px',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      />
    );
  }, [loading, filteredTools, stockColumnDefs, handleEditTool, handleDeleteTool]);

  // Contenido memoizado para la tabla de asignadas
  const renderAssignedTableContent = useMemo(() => {
    if (loadingAssigned && filteredAssignedTools.length === 0) {
      return (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando herramientas asignadas...</p>
        </div>
      );
    }

    if (filteredAssignedTools.length === 0) {
      return (
        <div className="text-center p-5">
          <FaCubes size={48} className="text-muted mb-3" />
          <p className="text-muted">No hay herramientas asignadas</p>
        </div>
      );
    }

    return (
      <TableWrapper
        ref={assignedGridRef}
        columnDefs={assignedColumnDefs}
        rowData={filteredAssignedTools}
        tableType="equipos-asignados"
        onGridReady={(e) => setAssignedGridApi(e.api)}
        customGridOptions={{
          animateRows: true,
          pagination: true,
          paginationPageSize: 25,
          domLayout: 'autoHeight',
          defaultColDef: {
            resizable: true,
            sortable: true,
            filter: true,
            floatingFilter: true
          }
        }}
        style={{ 
          marginBottom: '20px',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      />
    );
  }, [loadingAssigned, filteredAssignedTools, assignedColumnDefs]);

  return (
    <Container fluid>
      <Card className="mb-3">
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="mb-0">Agregar Herramienta</h5>
        </Card.Header>
        <Card.Body>
          <Row className="align-items-end">
            <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label>Nombre:</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={tool.name}
                  onChange={handleInputChange}
                  isInvalid={errors.name}
                  disabled={editMode}
                />
              </Form.Group>
            </Col>
            
            <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label>Tipo:</Form.Label>
                <Form.Select
                  name="type"
                  value={tool.type}
                  onChange={handleInputChange}
                  isInvalid={errors.type}
                  disabled={editMode}
                >
                  <option value="">Seleccione Tipo de Herramienta</option>
                  {TIPOS_EQUIPOS.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.descripcion}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col md={3}>
              <Form.Group className="mb-0">
                <Form.Label>Cantidad:</Form.Label>
                <Form.Control
                  type="number"
                  name="quantity"
                  value={tool.quantity}
                  onChange={handleInputChange}
                  isInvalid={errors.quantity}
                  ref={inputRef}
                />
              </Form.Group>
            </Col>
            
            <Col md={3} className="d-flex align-items-end">
              <Button
                variant={editMode ? "success" : "primary"}
                size="sm"
                className="px-3 py-2"
                onClick={handleSaveChanges}
                disabled={loading}
                style={{ height: '38px' }}
              >
                <FaSave className="me-1" />
                {editMode ? "Guardar Cambios" : "Agregar Herramientas"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Header className="bg-primary text-white text-center">
          <h5 className="mb-0">Gestión de Herramientas</h5>
        </Card.Header>

        <Card.Body className="p-0">
          <Tabs
            activeKey={activeTab}
            onSelect={handleTabChange}
            className="nav-tabs-custom"
          >
            {/* Pestaña de Stock */}
            <Tab 
              eventKey="stock" 
              title={
                <span>
                  <FaCubes className="me-1" />
                  Stock
                </span>
              }
            >
              {/* Barra de filtros para Stock */}
              <div className="d-flex justify-content-start p-2 bg-light border-bottom">
                <div className="d-flex gap-2">
                  <FilterSelect
                    options={nameOptions}
                    activeFilters={activeNames}
                    onFiltersChange={handleNameChange}
                    onOptionsUpdate={handleNameOptionsUpdate}
                    title="Filtrar por herramienta"
                    filterKey="tools-stock-names"
                    icon={<FaCubes />}
                  />
                  
                  <FilterSelect
                    options={typeOptions}
                    activeFilters={activeTypes}
                    onFiltersChange={handleTypeChange}
                    onOptionsUpdate={handleTypeOptionsUpdate}
                    title="Filtrar por tipo"
                    filterKey="tools-stock-types"
                    icon={<FaTags />}
                  />
                </div>
                <div className="ms-auto">
                  <ExportCsvButton gridApi={stockGridApi} moduleName="herramientas-stock" />
                </div>
              </div>

              <div className="p-0">
                {renderStockTableContent}
              </div>
            </Tab>

            {/* Pestaña de Asignadas */}
            <Tab 
              eventKey="assigned" 
              title={
                <span>
                  <FaUser className="me-1" />
                  Asignadas
                </span>
              }
            >
              {/* Barra de filtros para Asignadas */}
              <div className="d-flex justify-content-start p-2 bg-light border-bottom">
                <div className="d-flex gap-2">
                  <FilterSelect
                    options={assignedNameOptions}
                    activeFilters={activeAssignedNames}
                    onFiltersChange={handleAssignedNameChange}
                    onOptionsUpdate={handleAssignedNameOptionsUpdate}
                    title="Filtrar por herramienta"
                    filterKey="tools-assigned-names"
                    icon={<FaCubes />}
                  />
                  
                  <FilterSelect
                    options={assignedTypeOptions}
                    activeFilters={activeAssignedTypes}
                    onFiltersChange={handleAssignedTypeChange}
                    onOptionsUpdate={handleAssignedTypeOptionsUpdate}
                    title="Filtrar por tipo"
                    filterKey="tools-assigned-types"
                    icon={<FaTags />}
                  />

                  <FilterSelect
                    options={assignedToTypeOptions}
                    activeFilters={activeAssignedToTypes}
                    onFiltersChange={handleAssignedToTypeChange}
                    onOptionsUpdate={handleAssignedToTypeOptionsUpdate}
                    title="Filtrar por tipo de asignado"
                    filterKey="tools-assigned-to-types"
                    icon={<FaUser />}
                  />
                </div>
                <div className="ms-auto">
                  <ExportCsvButton gridApi={assignedGridApi} moduleName="herramientas-asignadas" />
                </div>
              </div>

              <div className="p-0">
                {renderAssignedTableContent}
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
      
      <CustomSnackbar
        open={snackbar.open}
        type={snackbar.type}
        message={snackbar.message}
        onClose={handleCloseSnackbar}
      />

      {/* Estilos CSS inline específicos para este componente */}
      <style jsx="true">{`
        .ag-root-wrapper {
          border-radius: 8px !important;
        }
        .ag-header {
          border-top-left-radius: 8px !important;
          border-top-right-radius: 8px !important;
        }
        .ag-paging-panel {
          border-bottom-left-radius: 8px !important;
          border-bottom-right-radius: 8px !important;
        }
        .nav-tabs-custom {
          border-bottom: 1px solid #dee2e6;
          background-color: #f8f9fa;
        }
        .nav-tabs-custom .nav-link {
          color: #495057;
          font-weight: 500;
        }
        .nav-tabs-custom .nav-link.active {
          font-weight: 600;
          border-bottom-color: #0d6efd;
        }
      `}</style>
    </Container>
  );
}

export default ToolsList;